import {
  FIRST_PANEL,
  LAST_PANEL,
  PreviewLabel,
  PreviewLayoutConfig,
  PreviewStructureItem,
  PreviewStructureItemType,
  SceneKey,
  ScenesMap,
} from './types';

// -------------------- Layout Keys --------------------
// LayoutKey: Enum representing the different supported booklet layout types.
// Each key corresponds to a specific layout configuration.
export enum SceneMapKey {
  PerfectBoundBooklet = 'perfect_bound_booklet',
  WiredBookletEU = 'wired_booklet_eu', // Wired Booklet with Sequential Pages
  WiredBookletNA = 'wired_booklet_na', // Wired Booklet with pages in order Outside front, back, inside front, inside back, inner pages
  // SaddleStitch = 'saddle_stitch', // Future support for Saddle Stitch booklets scene previews
}

export enum PreviewLayoutKey {
  SequentialPages = 'sequential_pages', // Layout for sequential pages
  TwoPanelCoversFollowingPages = 'two_panel_covers_following_pages', // Layout for pages with a panel each for outer covers and inner covers following pages
  FourPanelCoversFollowingPages = 'four_panel_covers_following_pages', // Layout for pages with two panels each for outer covers and inner covers following pages
}

// -------------------- Types for Config Maps --------------------
type SceneMapConfig = Record<SceneMapKey, ScenesMap>;
type PreviewLayoutConfigMap = Record<PreviewLayoutKey, PreviewStructureItem[]>;
export type ProductToLayoutMap = Record<string, PreviewLayoutConfig>;

export const sceneMapConfig: SceneMapConfig = {
  [SceneMapKey.PerfectBoundBooklet]: {
    [SceneKey.FrontCover]: { scene_name: 'Single Outer - Front Cover' },
    [SceneKey.InnerFrontCoverFirstPage]: { scene_name: 'Double page with left inner cover and right inner page' },
    [SceneKey.DoublePage]: { scene_name: 'Double inner pages' },
    [SceneKey.InnerBackCoverLastPage]: { scene_name: 'Double page with right inner cover and left inner page' },
    [SceneKey.BackCover]: { scene_name: 'Single Outer - Back Cover' },
  },
  [SceneMapKey.WiredBookletEU]: {
    [SceneKey.FrontCover]: { scene_name: 'Front Cover' },
    [SceneKey.InnerFrontCoverFirstPage]: { scene_name: 'Inner pages' },
    [SceneKey.DoublePage]: { scene_name: 'Inner pages' },
    [SceneKey.InnerBackCoverLastPage]: { scene_name: 'Inner pages' },
    [SceneKey.BackCover]: { scene_name: 'Back Cover' },
  },
  [SceneMapKey.WiredBookletNA]: {
    [SceneKey.FrontCover]: { scene_name: 'Outside Front Cover' },
    [SceneKey.InnerFrontCoverFirstPage]: { scene_name: 'Inside Front Cover and First Page' },
    [SceneKey.DoublePage]: { scene_name: 'Double Inside Pages' },
    [SceneKey.InnerBackCoverLastPage]: { scene_name: 'Inside Back Cover and Last Page' },
    [SceneKey.BackCover]: { scene_name: 'Outside Back Cover' },
  },
};

// -------------------- Layouts Configuration --------------------
export const previewLayoutMap: PreviewLayoutConfigMap = {
  [PreviewLayoutKey.SequentialPages]: [
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.FrontCover,
      panels: [FIRST_PANEL],
      labels: [PreviewLabel.FrontCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.DoublePage,
      panels: [FIRST_PANEL + 1, FIRST_PANEL + 2],
      labels: [PreviewLabel.InsideFrontCover, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Range,
      scene: SceneKey.DoublePage,
      panel_range: [FIRST_PANEL + 3, LAST_PANEL - 3],
      count: 2,
      labels: [PreviewLabel.Page, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.DoublePage,
      panels: [LAST_PANEL - 2, LAST_PANEL - 1],
      labels: [PreviewLabel.Page, PreviewLabel.InsideBackCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.BackCover,
      panels: [LAST_PANEL],
      labels: [PreviewLabel.BackCover],
    },
  ],
  [PreviewLayoutKey.TwoPanelCoversFollowingPages]: [
    {
      type: PreviewStructureItemType.Fixed,
      scene: null,
      panels: [FIRST_PANEL],
      labels: [PreviewLabel.BackCover, PreviewLabel.Spine, PreviewLabel.FrontCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.FrontCover,
      panels: [FIRST_PANEL],
      labels: [PreviewLabel.FrontCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.InnerFrontCoverFirstPage,
      panels: [FIRST_PANEL + 1, FIRST_PANEL + 2],
      labels: [PreviewLabel.InsideFrontCover, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Range,
      scene: SceneKey.DoublePage,
      panel_range: [FIRST_PANEL + 3, LAST_PANEL - 1],
      count: 2,
      labels: [PreviewLabel.Page, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.InnerBackCoverLastPage,
      panels: [LAST_PANEL, FIRST_PANEL + 1],
      labels: [PreviewLabel.Page, PreviewLabel.InsideBackCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.BackCover,
      panels: [FIRST_PANEL],
      labels: [PreviewLabel.BackCover],
    },
  ],
  [PreviewLayoutKey.FourPanelCoversFollowingPages]: [
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.FrontCover,
      panels: [FIRST_PANEL],
      labels: [PreviewLabel.FrontCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.InnerFrontCoverFirstPage,
      panels: [FIRST_PANEL + 2, FIRST_PANEL + 4],
      labels: [PreviewLabel.InsideFrontCover, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Range,
      scene: SceneKey.DoublePage,
      panel_range: [FIRST_PANEL + 5, LAST_PANEL - 1],
      count: 2,
      labels: [PreviewLabel.Page, PreviewLabel.Page],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.InnerBackCoverLastPage,
      panels: [LAST_PANEL, FIRST_PANEL + 3],
      labels: [PreviewLabel.Page, PreviewLabel.InsideBackCover],
    },
    {
      type: PreviewStructureItemType.Fixed,
      scene: SceneKey.BackCover,
      panels: [FIRST_PANEL + 1],
      labels: [PreviewLabel.BackCover],
    },
  ],
};

// -------------------- Product to Layout Mapping --------------------
// Used to determine which layout configuration to use for a given product.
export const productToLayoutMap: ProductToLayoutMap = {
  'PRD-ZJ6EMXNJY': {
    scenes: sceneMapConfig[SceneMapKey.PerfectBoundBooklet],
    orderedPreviewStructure: previewLayoutMap[PreviewLayoutKey.TwoPanelCoversFollowingPages],
  },
  'PRD-7HJNYRSTO': {
    scenes: sceneMapConfig[SceneMapKey.PerfectBoundBooklet],
    orderedPreviewStructure: previewLayoutMap[PreviewLayoutKey.TwoPanelCoversFollowingPages],
  },
  'PRD-E3SJ9Y0R': {
    scenes: sceneMapConfig[SceneMapKey.WiredBookletEU],
    orderedPreviewStructure: previewLayoutMap[PreviewLayoutKey.SequentialPages],
  },
  'PRD-SDBNXQAVH': {
    scenes: sceneMapConfig[SceneMapKey.WiredBookletNA],
    orderedPreviewStructure: previewLayoutMap[PreviewLayoutKey.SequentialPages],
  },
};
